/**
 * iDong 推送运营页共用常量与工具
 *
 * 说明：recordCourse（录播课推送，历史页面）仍保留其本地定义，此处仅收敛本次
 * AI 课/计划课页面之间的重复定义。
 */

// 推送状态（与 litta_external_course_sync_record.sync_status 一致）
export const SYNC_STATUS = {
  1: { text: '推送中', color: 'processing' },
  2: { text: '成功', color: 'success' },
  3: { text: '失败', color: 'error' },
};

export const STATUS_OPTIONS = [
  { label: '推送中', value: 1 },
  { label: '成功', value: 2 },
  { label: '失败', value: 3 },
];

// 运营批次：AI 课与老计划课共用同一套表与推送链路，仅此字段区分
export const PLAN_COURSE_TYPE = {
  PLAN_COURSE: { text: '计划课', color: 'default' },
  AI_COURSE: { text: 'AI课', color: 'blue' },
};

export const BATCH_OPTIONS = [
  { label: 'AI课', value: 'AI_COURSE' },
  { label: '计划课', value: 'PLAN_COURSE' },
];

// 器械/场景档位（与 litta InstrumentTypeEnum 一致）
export const INSTRUMENT_OPTIONS = [
  { label: '徒手训练', value: 'HANDS_ON' },
  { label: '居家小器械', value: 'SMALL_HOME' },
  { label: '健身房小器械', value: 'SMALL' },
  { label: '固定器械', value: 'FIXED' },
];

// 展示用文案（与 litta 侧枚举一一对应）
export const DIFFICULTY_TEXT = {
  ZERO_BASED: '零基础',
  ADVANCED: '进阶',
  STRENGTHEN: '强化',
};

export const EQUIPMENT_TEXT = {
  HANDS_ON: '徒手训练',
  SMALL_HOME: '居家小器械',
  SMALL: '健身房小器械',
  FIXED: '固定器械',
};

export const TARGET_TEXT = {
  FATIGUE_RELIEF: '疲劳改善',
  ENERGY_BOOST: '精力提升',
  VITALITY_CARDIO: '元气有氧',
  POSTURE_STABILITY: '体态稳定',
  FULL_BODY_WAKEUP: '全身唤醒',
  CARDIO_ACTIVATION: '心肺激活',
  POSTURE_STRENGTHEN: '体态强化',
  FULL_BODY_STRENGTH: '全身力量',
  CARDIO_ENDURANCE: '心肺耐力',
  BODY_TONING: '身型紧致',
  STRENGTH_SPRINT: '肌力冲刺',
  STRENGTH_BURST: '肌力爆发',
  BODY_SHAPING: '身型塑造',
  HEALTH_IMPROVEMENT: '健康改善',
  RAPID_FAT_BURN: '极速燃脂',
  MUSCLE_GAIN_SHAPING: '增肌塑形',
};

/**
 * 推送中（status=1）
 *
 * 用途：命中时交互上加二次确认（"强制推送"），<b>不再据此置灰按钮</b>。
 * 原因：渠道不回执时记录会一直停在"推送中"（列表侧要等
 * idong.plan.course.push.timeout.minutes 分钟才就地判超时），运营需要能立刻重推；
 * 重复推送由渠道按 courseId 幂等兜底，后端 push/repush 也不拦推送中。
 */
export const isPushing = (record) => record.latestSyncStatus === 1;

/**
 * 时间展示：后端可能给出非空但无法解析的值（零值时间、带 .0 的时间串、对象等），
 * 直接 new Date(v).toLocaleString() 会渲染成 "Invalid Date"，这里统一兜底成 '-'。
 */
export const formatTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
};

/** 课程阶段类型（与后端 TemplateCoursePhaseTypeEnum 一致：1 动作准备 → 6 放松拉伸） */
export const PHASE_TYPE_TEXT = {
  1: '动作准备',
  2: '力量循环组1',
  3: '力量循环组2',
  4: '力量循环组3',
  5: '力量循环组4',
  6: '放松拉伸',
};

export const PHASE_TYPE_OPTIONS = Object.keys(PHASE_TYPE_TEXT).map((key) => ({
  label: PHASE_TYPE_TEXT[key],
  value: Number(key),
}));

/** 场景分类（与后端 SystemEmpoweredPlanSceneTypeEnum 一致）：居家 / 健身房 */
export const SCENE_CATEGORY_TEXT = {
  HOME: '居家',
  GYM: '健身房',
};

export const SCENE_CATEGORY_OPTIONS = Object.keys(SCENE_CATEGORY_TEXT).map(
  (key) => ({
    label: SCENE_CATEGORY_TEXT[key],
    value: key,
  }),
);

/**
 * 场景子分类 → 场景分类：HANDS_ON/SMALL_HOME → HOME 居家；SMALL/FIXED → GYM 健身房
 * （与后端 ExternalCourseAttributeResolver#resolveSceneCategory 保持一致）
 */
export const resolveSceneCategory = (instrumentType) => {
  if (instrumentType === 'HANDS_ON' || instrumentType === 'SMALL_HOME') {
    return 'HOME';
  }
  if (instrumentType === 'SMALL' || instrumentType === 'FIXED') {
    return 'GYM';
  }
  return null;
};

/** 计划内课程的唯一键（推送接口用 courseType_courseIdent） */
export const courseKey = (record) =>
  `${record.courseType}_${record.courseIdent}`;
